<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import type { IEnergyTariff } from '@/interfaces/entities/management'

const props = defineProps<{
  isOpen: boolean
  editing?: IEnergyTariff | null
}>()

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'save', payload: Record<string, unknown>): void
}>()

const form = reactive({
  name: '',
  rate_peak_consumption: 0,
  rate_offpeak_consumption: 0,
  rate_peak_demand: 0,
  rate_offpeak_demand: 0,
  valid_from: '',
  valid_until: '' as string | null,
})
const error = ref('')

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    error.value = ''
    if (props.editing) {
      Object.assign(form, {
        name: props.editing.name,
        rate_peak_consumption: props.editing.rate_peak_consumption,
        rate_offpeak_consumption: props.editing.rate_offpeak_consumption,
        rate_peak_demand: props.editing.rate_peak_demand,
        rate_offpeak_demand: props.editing.rate_offpeak_demand,
        valid_from: props.editing.valid_from?.slice(0, 10) ?? '',
        valid_until: props.editing.valid_until?.slice(0, 10) ?? '',
      })
    } else {
      Object.assign(form, {
        name: '',
        rate_peak_consumption: 0,
        rate_offpeak_consumption: 0,
        rate_peak_demand: 0,
        rate_offpeak_demand: 0,
        valid_from: '',
        valid_until: '',
      })
    }
  },
)

const consumptionFields = [
  { key: 'rate_peak_consumption', label: 'Consumo ponta (R$/kWh)' },
  { key: 'rate_offpeak_consumption', label: 'Consumo fora ponta (R$/kWh)' },
  { key: 'rate_peak_demand', label: 'Demanda ponta (R$/kW)' },
  { key: 'rate_offpeak_demand', label: 'Demanda fora ponta (R$/kW)' },
] as const

function submit() {
  if (!form.name.trim()) {
    error.value = 'Informe o nome da tarifa.'
    return
  }
  if (!form.valid_from) {
    error.value = 'Informe o início da vigência.'
    return
  }
  emit('save', {
    name: form.name.trim(),
    rate_peak_consumption: Number(form.rate_peak_consumption),
    rate_offpeak_consumption: Number(form.rate_offpeak_consumption),
    rate_peak_demand: Number(form.rate_peak_demand),
    rate_offpeak_demand: Number(form.rate_offpeak_demand),
    valid_from: form.valid_from,
    valid_until: form.valid_until || null,
  })
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/40" @click="emit('cancel')" />
    <div class="relative w-full max-w-lg bg-white p-6 shadow-xl">
      <h3 class="text-lg font-semibold text-slate-900">
        {{ editing ? 'Editar tarifa' : 'Nova tarifa de energia' }}
      </h3>

      <form class="mt-4 space-y-4" @submit.prevent="submit">
        <div>
          <label class="mb-1 block text-sm font-medium text-slate-700">Nome</label>
          <input
            v-model="form.name"
            type="text"
            class="w-full border border-zinc-300 px-4 py-2.5 focus:border-primary focus:outline-none"
            placeholder="Ex.: Tarifa Verde 2026" />
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div v-for="field in consumptionFields" :key="field.key">
            <label class="mb-1 block text-sm font-medium text-slate-700">{{ field.label }}</label>
            <input
              v-model.number="form[field.key]"
              type="number"
              step="0.000001"
              min="0"
              class="w-full border border-zinc-300 px-4 py-2.5 focus:border-primary focus:outline-none" />
          </div>
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Início da vigência</label>
            <input
              v-model="form.valid_from"
              type="date"
              class="w-full border border-zinc-300 px-4 py-2.5 focus:border-primary focus:outline-none" />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Fim da vigência (opcional)</label>
            <input
              v-model="form.valid_until"
              type="date"
              class="w-full border border-zinc-300 px-4 py-2.5 focus:border-primary focus:outline-none" />
          </div>
        </div>

        <p v-if="error" class="text-sm text-red">{{ error }}</p>

        <div class="flex justify-end gap-3 border-t border-stroke pt-4">
          <button
            type="button"
            class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            @click="emit('cancel')">
            Cancelar
          </button>
          <button
            type="submit"
            class="bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
            Salvar
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
